/**
 * Product type — single source of truth for the frontend.
 *
 * Mirrors the canonical Python classifier at
 * ``backend/shared/inspector/classifiers.py::classify_product_type``.
 *
 * The five buckets reflect how a Lightspeed product row is shaped:
 *  - simple             — flat product, no variants, no composite BOM
 *  - variant_parent     — has variants; is itself the LS "group head"
 *  - variant_child      — has variant_parent_id; sits under a parent
 *  - composite_dosage   — composite linked to exactly one child product
 *                         (qty is irrelevant: 0.04, 1.0, 1.3 are all dosages)
 *  - composite_kit      — composite linked to two or more child products
 *
 * Consumers that already receive a ``product_type`` field (e.g. product usage
 * audit) just use the maps below. Consumers that only have raw product
 * fields can call ``classifyProductType``.
 */
import {
  Inventory2Outlined,
  AccountTreeOutlined,
  LabelOutlined,
  WidgetsOutlined,
  PieChartOutlined,
} from '@mui/icons-material'
import type { SvgIconComponent } from '@mui/icons-material'

export type ProductType =
  | 'simple'
  | 'variant_parent'
  | 'variant_child'
  | 'composite_kit'
  | 'composite_dosage'

/** Bucket label used by the audit's breakdown rows. The classifier still
 *  emits ``variant_parent`` / ``variant_child`` for individual product rows,
 *  but the breakdown folds both into ``variant`` (a single Variants row) so
 *  users see one entry per family-level concept. */
export type ProductTypeBucket =
  | 'simple'
  | 'variant'
  | 'composite_kit'
  | 'composite_dosage'

export interface ProductTypeMeta {
  short: string
  long: string
  IconComponent: SvgIconComponent
  /** Hex colour applied to the chip text and icon.
   *  ``null`` falls back to the MUI default (neutral grey). */
  colorHex: string | null
}

const VARIANT_COLOR = '#1976d2'
const COMPOSITE_COLOR = '#7b1fa2'

export const PRODUCT_TYPE_META: Record<ProductType | ProductTypeBucket, ProductTypeMeta> = {
  simple: {
    short: 'Standard',
    long: 'Standard Product',
    IconComponent: Inventory2Outlined,
    colorHex: null,
  },
  variant_parent: {
    short: 'Variant',
    long: 'Variant',
    IconComponent: LabelOutlined,
    colorHex: VARIANT_COLOR,
  },
  variant_child: {
    short: 'Variant',
    long: 'Variant',
    IconComponent: LabelOutlined,
    colorHex: VARIANT_COLOR,
  },
  variant: {
    short: 'Variants',
    long: 'Variants',
    IconComponent: AccountTreeOutlined,
    colorHex: VARIANT_COLOR,
  },
  composite_kit: {
    short: 'Kit',
    long: 'Composite (Kit)',
    IconComponent: WidgetsOutlined,
    colorHex: COMPOSITE_COLOR,
  },
  composite_dosage: {
    short: 'Dosage',
    long: 'Composite (Dosage)',
    IconComponent: PieChartOutlined,
    colorHex: COMPOSITE_COLOR,
  },
}

type ProductTypeAny = ProductType | ProductTypeBucket

export const PRODUCT_TYPE_SHORT_LABELS: Record<ProductTypeAny, string> =
  Object.fromEntries(
    (Object.keys(PRODUCT_TYPE_META) as ProductTypeAny[]).map((k) => [
      k,
      PRODUCT_TYPE_META[k].short,
    ]),
  ) as Record<ProductTypeAny, string>

export const PRODUCT_TYPE_LABELS: Record<ProductTypeAny, string> =
  Object.fromEntries(
    (Object.keys(PRODUCT_TYPE_META) as ProductTypeAny[]).map((k) => [
      k,
      PRODUCT_TYPE_META[k].long,
    ]),
  ) as Record<ProductTypeAny, string>

/** Canonical presentation order — mirrors the Python ``_BREAKDOWN_ORDER_INDEX``
 *  and ``PRODUCT_TYPE_ORDER``. Used wherever product-type rows/chips need
 *  deterministic ordering (sortable columns, breakdown table, summary cards). */
export const PRODUCT_TYPE_SORT_ORDER: Record<ProductTypeAny, number> = {
  simple: 0,
  composite_kit: 1,
  composite_dosage: 2,
  variant: 3,
  variant_parent: 3,
  variant_child: 4,
}

interface ProductTypeInput {
  is_composite?: boolean | null
  has_variants?: boolean | null
  variant_parent_id?: string | null
  composite_bom?: Record<string, number> | null
}

/**
 * Classify a product row into one of the five product-type buckets.
 * Mirrors the Python implementation — keep behaviour aligned.
 */
export const classifyProductType = (row: ProductTypeInput): ProductType => {
  if (row.is_composite) {
    const bom = row.composite_bom || {}
    if (Object.keys(bom).length === 1) {
      return 'composite_dosage'
    }
    return 'composite_kit'
  }
  if (row.variant_parent_id) {
    return 'variant_child'
  }
  if (row.has_variants) {
    return 'variant_parent'
  }
  return 'simple'
}
